import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { FleetService } from "services/FleetService";
import { ListingTable } from "components/listing/ListingTable";
import { FormTextInput, FormDateRangeInput, FormLocationSelector } from "components/form-fields";
import { useDebounce } from "hooks/useDebounce";
import moment from "moment";
import { Filter, FilterButton } from "components/filtering";
import { SetFiltering } from 'state/actions';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";
import { AssetStatus } from "components/status/AssetStatus";
import { InternalLink } from "components/navigation/InternalLink";
import { LinkService } from "services/LinkService";
import { Tooltip } from "components/tooltip";
import { Icon } from "@blueprintjs/core";
import { UserAccess } from "components/user-access/UserAccess";
import { PageRestricted } from "pages/errors/page-restricted";
import { ReportList } from "components/reporting/ReportList";
import { NotificationInline } from 'components/notifications';
import PropTypes from 'prop-types';
import { Money } from "components/formatting/Money";
import { DataDisplayRecord } from "components/data-display";
import { GridLayout } from "components/grid";
import { AssetHelperFunctions } from 'helpers';

const tableHeadersHub = [
    "ID/Registration",
    "",
    "Fleet Number",
    "Location"
];
const tableHeadersProfile = [
    "Event Name",
    "Event Status",
    "Event Date",
    "Completed Date",
    "Cost Name",
    "Supplier",
    "Labour",
    "Parts",
    "Sundries",
    "Total Cost"
];
const sortableHeadersHub = [
    { headerName: "ID/Registration", sortName: "Registration" },
    { headerName: "Fleet Number", sortName: "FleetNumber" },
    { headerName: "Location", sortName: "Location" }
];

const sortableHeadersProfile = [
    { headerName: "Event Name", sortName: "EventName" },
    { headerName: "Event Status", sortName: "EventStatus" },
    { headerName: "Event Date", sortName: "EventDate" },
    { headerName: "Completed Date", sortName: "CompletedDate" },
    { headerName: "Cost Name", sortName: "CostName" },
    { headerName: "Supplier", sortName: "Supplier" },
    { headerName: "Labour", sortName: "Labour" },
    { headerName: "Parts", sortName: "Parts" },
    { headerName: "Sundries", sortName: "Sundries" },
    { headerName: "Total Cost", sortName: "TotalCost" }
];

export function CostListing({ eventOptions, eventTypes, locationList, pageState = "PROFILE", asset, mappedAssetOptions }) {

    const dateFormat = "YYYY-MM-DD";
    const costListingFilterReduxName = "costListingFilterRedux" + pageState;
    const eventHubReduxName = "eventHubRedux" + pageState;
    const eventCostReduxName = "eventCostReduxName" + pageState;
    const defaultPageSize = 25;
    const halfYear = 6;
    const hubFilterCount = 5;
    const filterColumnCount = pageState === "HUB" ? hubFilterCount : 2;
    const [costs, setCosts] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [searchTerm, setSearchTerm] = useState(null);
    const [startDate, setStartDate] = useState(moment().subtract(halfYear, 'months').format(dateFormat));
    const [endDate, setEndDate] = useState(moment().format(dateFormat));
    const [pageFilters, setPageFilters] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [costTypeOptions, setCostTypeOptions] = useState([]);
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
    const debounceTimeoutMs = 750;
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeoutMs);
    const inputSearchRef = useRef(null);
    const dispatch = useDispatch();
    const eventHubRedux = useSelector(state => state.filters[eventHubReduxName]);
    const eventListingRedux = useSelector(state => state.filters[eventCostReduxName]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationDefaultsToTrue, setLocationDefaultsToTrue] = useState(null);
    const [reportDownloading, setReportDownloading] = useState(false);
    const [reduxLoaded, setReduxLoaded] = useState(false);
    const [eventListingReduxLoaded, setEventListingReduxLoaded] = useState(false);
    const [eventHubReduxLoaded, setEventHubReduxLoaded] = useState(false);
    const [eventListingFilterReduxLoaded, setEventListingFilterReduxLoaded] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const headers = pageState === "HUB" ? [...tableHeadersHub, ...tableHeadersProfile] : tableHeadersProfile;
    const sortableHeadersToUse = pageState === "HUB" ? [...sortableHeadersHub, ...sortableHeadersProfile] : sortableHeadersProfile;
    
    const [tableSortBy, setTableSortBy] = useState(sortableHeadersToUse[0]);
    const [tableSortDir, setTableSortDir] = useState("A");
    const [predefinedFilters, setPredefinedFilters] = useState([]);
    const [predefinedFiltersSetupComplete, setPredefinedFiltersSetupComplete] = useState(false);
    const eventListingFilterRedux = useSelector(state => state.filters[costListingFilterReduxName]) || [];

    if (eventListingFilterRedux.length === 0 && pageState === "HUB") {
        dispatch(SetFiltering(costListingFilterReduxName, AssetHelperFunctions.defaultAssetStatusFilters));
    }

    const assetTypeOptions = mappedAssetOptions?.assetTypes || [];
    const assetStatusOptions = mappedAssetOptions?.assetStatusesMappings || [];
    const assetOwnershipOptions = mappedAssetOptions?.ownershipAttributeMappings || [];
    const assetStatusesMappings = mappedAssetOptions?.assetStatusesMappings || [];
    const ownershipAttributeMappings = mappedAssetOptions?.ownershipAttributeMappings || [];

    const currentPageSize = useRef();
    const currentPageNumber = useRef();
    const currentSortBy = useRef();
    const currentSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentSearchTerm = useRef();
    const currentSelectedLocations = useRef();

    useEffect(() => {
        FleetService.getCosts({
            requestCount: 100,
            pageNumber: 1,
            searchTerm: "",
            filters: [{ key: "CategoryName", value: "asset" }]
        })
            .then(data => {
                if (data && data.data) {
                    setCostTypeOptions(
                        data.data.map(ct => ({ id: String(ct.id), name: ct.name }))
                    );
                }
            });
    }, []);

    useEffect(() => {
        if (pageState === 'HUB') {
            setPredefinedFiltersSetupComplete(true);
        }
        if (eventOptions && eventTypes) {
            const eventTypeFilters = Array.isArray(eventTypes)
                ? eventTypes.map(et => ({
                    id: String(et.eventTypeId ?? et.id),
                    name: et.eventTypeName ?? et.name
                }))
                : [];
            const allFilters = [
                {
                    name: "ASSETSTATUSID",
                    displayName: "Asset Status",
                    items: assetStatusOptions
                },
                {
                    name: "ASSETOWNERSHIPID",
                    displayName: "Asset Ownership",
                    items: assetOwnershipOptions
                },
                {
                    name: "ASSETTYPE",
                    displayName: "Asset Type",
                    items: assetTypeOptions
                },
                {
                    name: "EVENTTYPEID",
                    displayName: "Event Type",
                    items: eventTypeFilters
                },
                {
                    name: "COSTTYPEID",
                    displayName: "Cost Type",
                    items: costTypeOptions
                }

            ];
            if (pageState === "HUB") {
                setPageFilters(allFilters);
            } else {
                setPageFilters(allFilters.filter(f => !["ASSETSTATUSID", "ASSETOWNERSHIPID", "ASSETTYPE"].includes(f.name)));
            }
        }
    }, [eventOptions, eventTypes, costTypeOptions, mappedAssetOptions, appliedFilters, expandedAssetTypes, pageState]);

    useEffect(() => {
        if (((!reduxLoaded || !predefinedFiltersSetupComplete || (locationDefaultsToTrue && selectedLocations.length < 1)) ||
            //Need to do this check as we need to use the debouncedSearchTerm, but we need to only load once if the seachTerm is set in the redux so once it's set we know we have the redux all loaded
            searchTerm === null ||
            (searchTerm?.length > 0 && debouncedSearchTerm === null) ||
            !appliedFilters) ||
            currentPageSize.current === pageSize &&
            currentPageNumber.current === pageNumber &&
            currentSortBy.current === tableSortBy &&
            currentSortDir.current === tableSortDir &&
            currentAppliedFilters.current === appliedFilters.length &&
            currentStartDate.current === startDate &&
            currentEndDate.current === endDate &&
            currentSelectedLocations.current === selectedLocations &&
            (currentSearchTerm.current ?? "") === (debouncedSearchTerm ?? "")
        ) {
            return;
        }

        if (pageNumber > 1 && (currentSearchTerm.current ?? "") !== (debouncedSearchTerm ?? "")) {
            //Can't set the page number when this changes else the debounce doesn't work. The page number change should kick this off again.
            setPageNumber(1);
            return;
        }

        setLoading(true);

        currentPageSize.current = pageSize;
        currentPageNumber.current = pageNumber;
        currentSortBy.current = tableSortBy;
        currentSortDir.current = tableSortDir;
        currentAppliedFilters.current = appliedFilters.length;
        currentStartDate.current = startDate;
        currentEndDate.current = endDate;
        currentSelectedLocations.current = selectedLocations;
        currentSearchTerm.current = (debouncedSearchTerm ?? "");

        let filters = [];
        if (pageState === "HUB") {
            if (selectedLocations && selectedLocations.length > 0) {
                filters.push(...selectedLocations.map(locId => ({ key: "Location", value: String(locId) })));
            } else {
                filters.push({ key: "Location", value: "-1" });
            }
        } else {
            filters.push(...predefinedFilters);
        }
        if (appliedFilters && appliedFilters.length > 0) {
            filters.push(...appliedFilters.map(f => {
                if (f.name === "AssetTypeSubtype" && typeof f.value === "string" && f.value.startsWith("uncat-")) {
                    return { key: "AssetType", value: f.parentAssetTypeId.toString() };
                }
                return { key: f.name, value: String(f.value) };
            }));
        }
        FleetService.searchEventCosts(
            pageSize,
            pageNumber,
            debouncedSearchTerm,
            tableSortBy.sortName,
            tableSortDir,
            startDate,
            endDate,
            filters,
        ).then(data => {
                setCosts(data);
                setLoading(false);
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
            })
            .catch(error => {
                NotificationToaster.show(Intent.DANGER, "Could not fetch costs, please try again.");
            })
            .finally(() => {
                setLoading(false);
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
            });
    }, [pageNumber, pageSize, debouncedSearchTerm, startDate, endDate, selectedLocations, appliedFilters, tableSortBy, tableSortDir, pageState, asset, predefinedFiltersSetupComplete, reduxLoaded]);

    useEffect(() => {
        if (pageState !== 'HUB' && asset?.assetLocation?.location?.id) {
            setPredefinedFilters([
                { key: "AssetId", value: `${asset.assetId}` },
                { key: "Location", value: `${asset?.assetLocation?.location?.id}` },
            ]);
        }
    }, [asset, pageState]);

    useEffect(() => {
        if (predefinedFilters && predefinedFilters.length > 0) {
            setPredefinedFiltersSetupComplete(true);
        }
    }, [predefinedFilters]);

    useEffect(() => {
        if (eventListingFilterReduxLoaded && eventHubReduxLoaded && eventListingReduxLoaded) {
            setReduxLoaded(true);
        }
    }, [eventListingFilterReduxLoaded, eventHubReduxLoaded, eventListingReduxLoaded]);

    useEffect(() => {
        if (!eventListingFilterReduxLoaded) {
            setAppliedFilters(eventListingFilterRedux ?? []);
            setEventListingFilterReduxLoaded(true);
        }
    }, [eventListingFilterRedux]);

    useEffect(() => {
        if (!eventHubReduxLoaded) {
            setEventHubReduxLoaded(true);
            if (eventHubRedux) {
                setStartDate(eventHubRedux.startDate);
                setEndDate(eventHubRedux.endDate);
            }

            if (pageState === 'HUB') {
                if (eventHubRedux) {
                    setLocationDefaultsToTrue(false);
                    setSelectedLocations(eventHubRedux.locations);
                } else {
                    setLocationDefaultsToTrue(true);
                }
            }
        }
    }, [eventHubRedux]);

    useEffect(() => {
        if (!eventListingReduxLoaded) {
            setEventListingReduxLoaded(true);
            if (eventListingRedux) {
                setPageNumber(eventListingRedux.pageNumber);
                setPageSize(eventListingRedux.pageSize);
                setTableSortDir(eventListingRedux.tableSortDir);
                setTableSortBy(eventListingRedux.tableSortBy);
                setSearchTerm(eventListingRedux.searchTerm);
            } else {
                setSearchTerm("");
            }
        }
    }, [eventListingRedux]);

    function mapCostsToTableRows(data) {
        if (!data) return [];
        return data.map((cost, index) => {
            let row = [];
            if (pageState === "HUB") {
                row.push (
                    <div className="nowrap inline-items"
                        key={`${cost.assetId}-${index}`}>
                        <AssetStatus
                            statusId={cost.assetStatus?.id}
                            ownershipId={cost.assetOwnership?.id}
                            isLoading={loading}
                            statusAttributeMappings={assetStatusesMappings}
                            ownershipAttributeMappings={ownershipAttributeMappings}
                            tooltipPosition="right"
                        />
                        <InternalLink link={LinkService.getVehicleProfileUrl(cost.assetId, 'events/costs', 'area=MMFH')}>
                            {cost.registration}
                        </InternalLink>
                    </div>,
                    <div className="nowrap inline-items" key={`asset-icon-${cost.assetId}-${index}`}>
                        {
                            AssetHelperFunctions.getVehicleIcon(
                                cost.assetType?.id,
                                cost.assetType?.name,
                                cost.assetSubtype?.name)
                        }
                    </div>,
                    cost.fleetNumber || "",
                    cost.assetLocation?.name || ""
               );
            }

            row.push(
                    cost.eventType?.name || "",
                    cost.eventStatus?.name || "",
                    cost.eventDate ? new Date(cost.eventDate).toLocaleDateString() : "",
                    cost.completedDate ? new Date(cost.completedDate).toLocaleDateString() : "",
                    cost.costType?.name || "",
                    cost.supplier?.name || "",
                    <Money key={`money-labour-${cost.assetId}-${index}`}>{cost.labour}</Money>,
                    <Money key={`money-parts-${cost.assetId}-${index}`}>{cost.parts}</Money>,
                    <Money key={`money-sundries-${cost.assetId}-${index}`}>{cost.sundries}</Money>,
                    <Money key={`money-total-${cost.assetId}-${index}`}>{cost.totalCost}</Money>
            );
            return row;

        });
    }

    function setEventListingDispatch(newSearchTerm, newPageNumber, newPageSize, newTableSortBy, newTableSortDir) {
        dispatch(SetFiltering(eventCostReduxName, {
            searchTerm: newSearchTerm ?? searchTerm,
            pageNumber: newPageNumber ?? pageNumber,
            pageSize: newPageSize ?? pageSize,
            tableSortBy: newTableSortBy ?? tableSortBy,
            tableSortDir: newTableSortDir ?? tableSortDir
        }));
    }

    function setEventHubDispatch(newStartDate, newEndDate, newLocations) {
        dispatch(SetFiltering(eventHubReduxName, {
            startDate: newStartDate ?? startDate,
            endDate: newEndDate ?? endDate,
            locations: newLocations ?? selectedLocations
        }));
    }

    function onSearchTextChange(e) {
        setSearchTerm(e.target.value);
        setEventListingDispatch(e.target.value);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
        setEventListingDispatch(null, newPageNumber, newPageSize);
    }

    function onSort(header, direction) {
        setTableSortBy(header);
        setTableSortDir(direction);
        setEventListingDispatch(null, null, null, header, direction);
    }

    function onDateRangeChange(item) {
        const tempStartDate = item.startDate.date ? item.startDate.date.format(dateFormat) : "";
        const tempEndDate = item.endDate.date ? item.endDate.date.format(dateFormat) : "";
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
        setEventHubDispatch(tempStartDate, tempEndDate);
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setEventHubDispatch(null, null, locations);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function onFilterChange(listingFilters) {
        if (initialLoadComplete) {
            setPageNumber(1);
        }
        setAppliedFilters(listingFilters ?? []);
        dispatch(SetFiltering(costListingFilterReduxName, listingFilters ?? []));
    }

    function onToggleFilter() {
        setShowFilters((prevState) => !prevState);
    }

    const selectionLabour = costs?.selectionLabour ?? 0;
    const selectionParts = costs?.selectionParts ?? 0;
    const selectionSundries = costs?.selectionSundries ?? 0;
    const selectionTotalCost = costs?.selectionTotalCost ?? 0;
    const hasMultiplePages = (costs?.totalCount || 0) > pageSize;

    return (
        <UserAccess
            perform="FMS:View"
            yes={() => (
                <>
                    <NotificationInline
                        intent="success"
                        text="Your report has been queued, and will download as soon as it's ready."
                        show={reportDownloading}
                    />
                    <GridLayout numberOfColumns={6} gap={30} className="spacer-bottom">
                        <DataDisplayRecord
                            header="Labour"
                            data={(
                                <Money>{selectionLabour}</Money>
                            )}
                            loading={loading}
                        />
                        <DataDisplayRecord
                            header="Parts"
                            data={(
                                <Money>{selectionParts}</Money>
                            )}
                            loading={loading}
                        />
                        <DataDisplayRecord
                            header="Sundries"
                            data={(
                                <Money>{selectionSundries}</Money>
                            )}
                            loading={loading}
                        />
                        <DataDisplayRecord
                            header="Total Cost"
                            data={(
                                <>
                                    <Money>{selectionTotalCost}</Money>
                                    {hasMultiplePages && !loading && (
                                        <Tooltip content="These totals represent all rows, not just those shown on the current page.">
                                            <Icon
                                                icon="info-sign"
                                                style={
                                                    {
                                                        marginLeft: 6,
                                                        marginBottom: 3,
                                                        color: '#5c7080',
                                                        verticalAlign: 'middle'
                                                    }} />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            loading={loading}
                        />
                    </GridLayout>
                    <div className="filter-controls inline-items spacer-bottom-small">
                        <FormDateRangeInput
                            onChange={onDateRangeChange}
                            startDate={startDate}
                            endDate={endDate}
                            disabled={loading}
                            allowClear={false}
                            large
                        />
                        {pageState === "HUB" && (
                            <div className="inline-item fill">
                                <div className="form-field-full">
                                    <FormTextInput
                                        inputRef={inputSearchRef}
                                        placeholder="Search fleet"
                                        onChange={onSearchTextChange}
                                        value={searchTerm}
                                        large
                                        disabled={loading}
                                        icon="search"
                                        id="cost-search-field"
                                    />
                                </div>
                            </div>
                        )}
                        {pageState === 'HUB' && locationDefaultsToTrue !== null && (
                            <FormLocationSelector
                                businessArea=""
                                loading={loading}
                                selectedLocations={selectedLocations}
                                setSelectedLocations={onSelectedLocationsChange}
                                useHierarchy={false}
                                useCustomLocations
                                customLocations={locationList}
                                useLocationDefaults={locationDefaultsToTrue}
                                locationDefaultsToTrue={locationDefaultsToTrue}
                                large
                            />
                        )}
                        <ReportList
                            pageName="Cost-Listing"
                            lastSearchPayload={{
                                assetId: asset?.assetId,
                                startDate,
                                endDate,
                                appliedFilters,
                                searchTerm: pageState === "HUB" ? searchTerm : undefined,
                                selectedLocations: pageState === "HUB" ? selectedLocations : undefined
                            }}
                            onReportDownloadingChange={setReportDownloading}
                            disabled={loading}
                            compactLarge
                        />
                        <Filter
                            filterName={costListingFilterReduxName}
                            visible={showFilters && !!eventOptions && !!eventTypes}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            onClickOutside={onToggleFilter}
                            numOfColumns={filterColumnCount}
                            customRenderers={{
                                ASSETTYPE: (props) => (
                                    <AssetTypeFilterRenderer
                                        key="asset-Type-filter-renderer"
                                        {...props}
                                        expandedAssetTypes={expandedAssetTypes}
                                        setExpandedAssetTypes={setExpandedAssetTypes}
                                    />
                                )
                            }}
                            loading={loading}
                            disabled={loading}
                        />
                         <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                    <ListingTable
                        headers={headers}
                        data={mapCostsToTableRows(costs?.data)}
                        totalRecordCount={costs?.totalCount || 0}
                        onPagingChange={onPagingChange}
                        loadingData={loading}
                        pageSize={pageSize}
                        startPage={pageNumber}
                        noDataMessage={loading ? undefined : "No results found, please try altering the search criteria."}
                        sortable
                        sortableHeaders={sortableHeadersToUse}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onSort}
                    />
                </>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
CostListing.propTypes = {
    asset: PropTypes.object,
    eventOptions: PropTypes.object,
    eventTypes: PropTypes.array,
    locationList: PropTypes.any,
    pageState: PropTypes.string,
    mappedAssetOptions: PropTypes.object
};